import type {
  MemoryFeastAction,
  MemoryFeastState,
  OpenBowlsAction,
  PlaceTokensAction,
  ValidationResult,
} from "../types";
import { isBowlId, isPlacementPhase, isPlayerId, roundTokens } from "../utils";

const ok: ValidationResult = { valid: true };

function fail(
  error: string,
  details?: Record<string, unknown>,
): ValidationResult {
  return { valid: false, error, details };
}

function validateActor(
  state: MemoryFeastState,
  playerId: string,
): ValidationResult {
  if (!isPlayerId(playerId)) return fail("Unknown player", { playerId });
  if (state.phase === "game-over") return fail("Game is already over");
  if (state.currentPlayerId !== playerId) {
    return fail("It is not this player's turn", {
      playerId,
      currentPlayerId: state.currentPlayerId,
    });
  }
  return ok;
}

function allDistinct(ids: readonly number[]): boolean {
  return new Set(ids).size === ids.length;
}

export function validatePlaceTokens(
  state: MemoryFeastState,
  action: PlaceTokensAction,
): ValidationResult {
  const actor = validateActor(state, action.playerId);
  if (!actor.valid) return actor;
  if (!isPlacementPhase(state.phase)) {
    return fail("Placement is only allowed during the place phase");
  }
  const expected = roundTokens(state);
  if (action.bowlIds.length !== expected) {
    return fail("Must place exactly this round's token budget", {
      expected,
      received: action.bowlIds.length,
    });
  }
  if (!action.bowlIds.every(isBowlId)) {
    return fail("Bowl id out of range", { bowlIds: action.bowlIds });
  }
  if (!allDistinct(action.bowlIds)) {
    return fail("Cannot place more than one token in a bowl this turn", {
      bowlIds: action.bowlIds,
    });
  }
  return ok;
}

export function validateOpenBowls(
  state: MemoryFeastState,
  action: OpenBowlsAction,
): ValidationResult {
  const actor = validateActor(state, action.playerId);
  if (!actor.valid) return actor;
  if (state.phase !== "open") {
    return fail("Bowls can only be opened during the open phase");
  }
  if (action.bowlIds.length !== 2) {
    return fail("Must open exactly two bowls", { bowlIds: action.bowlIds });
  }
  if (!action.bowlIds.every(isBowlId)) {
    return fail("Bowl id out of range", { bowlIds: action.bowlIds });
  }
  if (!allDistinct(action.bowlIds)) {
    return fail("Must open two distinct bowls", { bowlIds: action.bowlIds });
  }
  return ok;
}

export function validateAction(
  state: MemoryFeastState,
  action: MemoryFeastAction,
): ValidationResult {
  switch (action.type) {
    case "PlaceTokens":
      return validatePlaceTokens(state, action);
    case "OpenBowls":
      return validateOpenBowls(state, action);
  }
}
